import React, { useState } from 'react';
import * as z from 'zod';
import {
  ArrowRight, Backpack, BadgeCheck, LucideSkipBack, LucideStepBack, SendToBackIcon,
  SkipBack,
  StepBack,
  StepForward,
  Undo2
} from 'lucide-react';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import ServiceCertificateRequestPrint from './salary-certificate-print';

const formSchema = z.object({
  type: z.string({
    required_error: 'يرجى اختيار نوع الراتب',
  }).min(1, "نوع الراتب مطلوب"),
  presentedTo: z.string({
    required_error: '(انكليزي) الجهة المقدم لها تعريف الراتب',
  }).min(1, "الجهة المقدم لها تعريف الراتب مطلوب (انكليزي)"),
  presentedToAr: z.string({
    required_error: '(عربي) الجهة المقدم لها تعريف الراتب',
  }).min(1, "الجهة المقدم لها تعريف الراتب مطلوب (عربي)"),

});


const ServiceCertificateRequest = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: '',
      presentedTo: '',
      presentedToAr: ''

    },
  });
  function onSubmit(values: z.infer<typeof formSchema>) {
    console.log(values);
    setIsSaved(true);
    // toast({
    //   title: 'تم إرسال الطلب بنجاح',
    //   description: 'سيتم مراجعة طلب تعريف الراتب والرد عليك قريباً.',
    // });
    
    setTimeout(() => {
      console.log("Redirecting to print page...");
      navigate("/services/salary-certificate-print", {
        replace: true, state: {
          employeeData: form.getValues()
        }
      });
    }, 2000);

    // Navigate back to services or requests page
    // navigate('/services');

  }
  const handlePrint = () => {
    // setShowPrintView(true);
    setTimeout(() => {
      console.log("Redirecting to print page...");
      navigate("/services/salary-certificate-print", {
        replace: true, state: {
          employeeData: form.getValues()
        }
      });
    }, 2000);
  };
  const handleClosePrintView = () => {
    console.log('closed')
    setShowPrintView(false);
  };


  if (showPrintView) {
    return (
      <div>
        {/* <ServiceCertificateRequestPrint employeeData={form.getValues()} /> */}
        <div className="bottom-4 flex justify-center">
          <button
            onClick={handleClosePrintView}
            className="w-48 bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <Undo2 className="h-4 w-4" />
            العودة إلى التعديل
          </button>
        </div>
      </div>
    );
  }

  const salaryTypes = [
    { value: 'total', label: 'كامل' },
    { value: 'detailed', label: 'مفصل' },
  ];



  return (
    <div className="w-full max-w-4xl">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/services')}
          className="gap-2"
        >
          <ArrowRight className="h-4 w-4" />
          العودة للخدمات
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-right">{t('services-salary-certificate.title')}</CardTitle>
          <CardDescription className="text-right">
            يرجى ملء جميع البيانات المطلوبة لتقديم {t('services-salary-certificate.title')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid  gap-6">
                <FormField
                  control={form.control}
                  name="type"
                  rules={{
                    required: "نوع الراتب is required." // Custom error message
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-right block">نوع الراتب</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger dir="rtl" className="text-right">
                            <SelectValue placeholder="اختر نوع الراتب" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent dir="rtl" className="text-right">
                          {salaryTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="presentedToAr"
                  rules={{
                    required: "الجهة المقدم لها تعريف الراتب (عربي) is required." // Custom error message
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-right block">الجهة المقدم لها تعريف الراتب (عربي)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="الجهة المقدم لها تعريف الراتب..."
                          className="text-right"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="presentedTo"
                  rules={{
                    required: "الجهة المقدم لها تعريف الراتب (انكليزي) is required." // Custom error message
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-right block">الجهة المقدم لها تعريف الراتب (انكليزي)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="The entity to which the salary definition is submitted..."
                          className="text-right"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>


              <div className="flex gap-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/services')}
                >
                  إلغاء
                </Button>
                {/* <Button type="submit" disabled={isSaved}>
                  حفظ
                </Button> */}
                <Button type="submit"
                  // onClick={handlePrint}
                  >
                  طباعة
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div >
  );
};

export default ServiceCertificateRequest;